"use client";

import { useQuery } from "convex/react";
import type { FunctionReturnType } from "convex/server";
import type { Id } from "@convex/_generated/dataModel";
import { api } from "@convex/_generated/api";
import { ORDER_STAGE } from "@convex/constants/statuses";
import { LEAD_STATUS } from "@convex/constants/leads";

export interface ActivityFeedData {
  vendorActionItems?: FunctionReturnType<typeof api.dashboards.actionItems.getActionItems>;
  quotedLeads?: FunctionReturnType<typeof api.leads.buyerQueries.listForCompany>;
  pendingOrders?: FunctionReturnType<typeof api.orders.queries.main.list>;
  carrierCounts?: FunctionReturnType<typeof api.freight.carrierDashboard.getCarrierNavCounts>;
  companyReadiness?: FunctionReturnType<typeof api.profileCompletion.queries.getCompanyReadiness>;
  ticketsAwaiting?: FunctionReturnType<typeof api.support.queries.listMyTicketsAwaitingResponse>;
}

interface ActivityFeedAudience {
  enabled: boolean;
  isVendor: boolean;
  isBuyer: boolean;
  isFreight: boolean;
  companyId: Id<"companies"> | undefined;
}

export function useActivityFeedData(
  { enabled, isVendor, isBuyer, isFreight, companyId }: ActivityFeedAudience,
): ActivityFeedData {
  const vendorActionItems = useQuery(
    api.dashboards.actionItems.getActionItems,
    enabled && isVendor && companyId ? { companyId } : "skip",
  );
  const quotedLeads = useQuery(
    api.leads.buyerQueries.listForCompany,
    enabled && isBuyer ? { status: LEAD_STATUS.QUOTED, limit: 5 } : "skip",
  );
  const pendingOrders = useQuery(
    api.orders.queries.main.list,
    enabled && isBuyer ? { stage: ORDER_STAGE.AWAITING_PICKUP, limit: 5 } : "skip",
  );
  const carrierCounts = useQuery(
    api.freight.carrierDashboard.getCarrierNavCounts,
    enabled && isFreight ? {} : "skip",
  );
  const companyReadiness = useQuery(
    api.profileCompletion.queries.getCompanyReadiness,
    enabled && (isVendor || isBuyer) ? {} : "skip",
  );
  const ticketsAwaiting = useQuery(
    api.support.queries.listMyTicketsAwaitingResponse,
    enabled ? {} : "skip",
  );
  return { vendorActionItems, quotedLeads, pendingOrders, carrierCounts, companyReadiness, ticketsAwaiting };
}

export function activityFeedDataLoading(
  { isVendor, isBuyer, isFreight }: Pick<ActivityFeedAudience, "isVendor" | "isBuyer" | "isFreight">,
  data: ActivityFeedData,
): boolean {
  return ((isVendor || isBuyer) && data.companyReadiness === undefined) ||
    data.ticketsAwaiting === undefined ||
    (isVendor && data.vendorActionItems === undefined) ||
    (isBuyer && (data.quotedLeads === undefined || data.pendingOrders === undefined)) ||
    (isFreight && data.carrierCounts === undefined);
}

function currentValue<Value>(live: Value | undefined, seed: Value | undefined): Value | undefined {
  return live === undefined ? seed : live;
}

export function initialActivityFeedData(live: ActivityFeedData, seed?: ActivityFeedData): ActivityFeedData {
  return {
    vendorActionItems: currentValue(live.vendorActionItems, seed?.vendorActionItems),
    quotedLeads: currentValue(live.quotedLeads, seed?.quotedLeads),
    pendingOrders: currentValue(live.pendingOrders, seed?.pendingOrders),
    carrierCounts: currentValue(live.carrierCounts, seed?.carrierCounts),
    companyReadiness: currentValue(live.companyReadiness, seed?.companyReadiness),
    ticketsAwaiting: currentValue(live.ticketsAwaiting, seed?.ticketsAwaiting),
  };
}
